import { useMemo } from 'react';
import type { VehiclePhotoDamageData } from '@ltvco/refresh-lib/api';
import type { Message } from '@ltvco/refresh-lib/v2';
import { MessagePairType } from 'components/ChatAI/VehicleMatchmaker/hooks/useMessagePairing';

type MessageWithPhotoDamage = Message;

type UseMessagePairingReturn = {
  pairs: MessagePairType[];
  firstPhotoDamageAssistantIndex: number;
  firstPhotoDamageEstimate: VehiclePhotoDamageData | null | undefined;
};

export const useMessagePairing = (
  messages: Message[],
): UseMessagePairingReturn => {
  return useMemo(() => {
    const messagesWithPhotoDamage = messages as MessageWithPhotoDamage[];

    const firstPhotoDamageAssistantIndex = messagesWithPhotoDamage.findIndex(
      (message) =>
        message.role === 'assistant' &&
        message.photoDamageEstimate?.damage_category !== undefined,
    );

    const firstPhotoDamageEstimate =
      firstPhotoDamageAssistantIndex !== -1
        ? messagesWithPhotoDamage[firstPhotoDamageAssistantIndex]
            .photoDamageEstimate
        : null;

    const pairs: MessagePairType[] = [];
    for (let i = 0; i < messagesWithPhotoDamage.length; i++) {
      const userMsg = messagesWithPhotoDamage[i];
      if (userMsg.role !== 'user') continue;

      const maybeAssistant = messagesWithPhotoDamage[i + 1];
      const assistantMsg =
        maybeAssistant && maybeAssistant.role === 'assistant'
          ? maybeAssistant
          : null;
      const assistantIndex = assistantMsg ? i + 1 : null;

      // Skip rendering the pair whose assistant message is rendered
      // as the DamageAnalyzerResultCard to avoid extra vertical spacing.
      if (
        assistantIndex !== null &&
        assistantIndex === firstPhotoDamageAssistantIndex
      ) {
        continue;
      }

      pairs.push({
        id: `pair-${i}`,
        user: userMsg,
        assistant: assistantMsg,
        userIndex: i,
        assistantIndex,
      });
    }

    return {
      pairs,
      firstPhotoDamageAssistantIndex,
      firstPhotoDamageEstimate,
    };
  }, [messages]);
};
